// =========================================================
// The page's own copy of story, day, how, who and faq: what shows until someone
// edits a section under «محتوى الصفحة الرئيسية», and what the editor opens on.
// The CMS value wins whenever one exists (GET /api/home-content).
//
// Every sentence is a product fact. The evidence for each lives in the
// codebase, and the ones that are easy to overstate say so beside them:
//
//   • trial length              Back-End/utils/plans.js TRIAL_DAYS (30)
//   • no grace after the trial  Back-End/utils/subscriptionState.js
//   • 7-day grace, paid only    subscriptionState.js GRACE_DAYS
//   • no auto-renewal           Controller/Payments.js — one invoice per period
//   • verification optional     Pages/Auth/AuthPage.js step 3
//   • tracking only on shift    utils/employeeTracking.js
//   • technicians see no money  Controller/MyWork.js field-safe columns
//   • audit log = key actions   utils/auditCatalog.js (not "every action")
//
// Words, not letters, are animated on this page — so no line here may
// rely on letter-spacing or per-letter effects.
// =========================================================

export const STORY = {
  eyebrow: "قبل الريّس",
  text: "المشوار في الواتساب. العهدة في دفتر. الفلوس في إكسل. وآخر اليوم بتلف على الأسطوات بالتليفون عشان تعرف مين خلّص إيه ومين قبض كام.",
  punch: "الشغل اللي بيضيع في الواتساب — بيتجمّع في الريّس.",
  // The owner's tools today, drawn as scattered cards that converge into one.
  // The lines are illustrations of the old way, not product claims.
  tools: [
    { id: "chat", label: "واتساب", line: "ابعتلي العنوان تاني؟" },
    { id: "book", label: "الدفتر", line: "2 كمبروسر عهدة… مع مين؟" },
    { id: "sheet", label: "إكسل", line: "حسابات نهائي 3" },
    { id: "calls", label: "المكالمات", line: "4 مكالمات فايتة" },
  ],
  // What they become — the four fixed module names (BRAND.md §5).
  oneScreen: "كل ده في شاشة واحدة",
  modules: ["المشاوير", "الأسطوات", "العهدة", "الخزنة"],
};

// `screen` names a real capture (jobs / stock / invoices) or one of the two
// drawn screens (mywork / push). The drawn ones quote only labels that exist
// in the product and show bars where data would be — never invented names
// or numbers.
export const DAY = {
  eyebrow: "يوم كامل",
  title: "من الصبح لحد آخر إشعار بالليل.",
  subtitle: "كل محطة شاشة من جوّه الريّس، بالموديول اللي بيشغّلها.",
  items: [
    {
      id: "jobs",
      time: "7:30",
      module: "المشاوير",
      title: "افتح المشوار ووزّعه، ومتسيبش حاجة في الواتساب.",
      body: "الطلب بيتفتح، يروح لأسطى أو أكتر، ويرجعلك للمراجعة قبل ما يتقفل.",
      points: [
        "قيد الانتظار، قيد التنفيذ، بانتظار المراجعة، مكتمل",
        "أكتر من أسطى على نفس المشوار",
        "كل دفعة وكل قسط في سجل المشوار",
      ],
      screen: "jobs",
    },
    {
      id: "techs",
      time: "9:00",
      module: "الأسطوات",
      title: "الأسطى بيشوف شغله من موبايله. وإنت شايف كل حاجة.",
      body: "يفتح «مهامي» من المتصفح، يشوف المشوار وعنوان الزبون، ويحرّك الشغل لقدّام. ومش بيشوف فلوس الشركة.",
      points: [
        "حضور وانصراف من الموبايل، وممكن تربطه بمسافة من المقر",
        "مكانه على الخريطة وقت الحضور بس، وبيقف لوحده عند الانصراف",
        "سلفة أو إجازة بيطلبها من موبايله، والسلفة لما توافق عليها بتتقيّد في الرواتب لوحدها",
      ],
      screen: "mywork",
    },
    {
      id: "stock",
      time: "11:00",
      module: "العهدة والتجار والموردين",
      title: "كل قطعة: مع مين، وامتى نزلت، وبكام.",
      body: "المخزون مبني على التكييف: الحصان، بارد أو بارد ساخن أو انفرتر، سبليت أو صندوقي أو حائطي أو كونسيلد. والعهدة عند التاجر ليها كشف حساب، مش ورقة.",
      points: [
        "تنبيه لما صنف يقرب يخلص",
        "حساب جاري لكل تاجر ومورد بيتحدّث لوحده",
        "التاجر يدخل بنفسه يشوف كشف حسابه ويطلب أجهزة",
      ],
      screen: "stock",
    },
    {
      id: "money",
      time: "3:00",
      module: "الخزنة",
      title: "فاتورة بتصميم شركتك، وقيد بيتسجّل لوحده.",
      body: "أربع تصميمات للفاتورة — الملكي، العاجي، الأسود الفاخر، أتيليه. وكل بيعة وكل دفعة وكل حركة مخزون بتتسجّل لوحدها في دفتر الأستاذ.",
      points: [
        "نقدي، آجل، وأقساط لحد 12 قسط",
        "ضريبة 14% لما تسجّل رقمك الضريبي",
        "أي رقم في الحسابات تفتحه وتوصل للقيد اللي جابه",
      ],
      screen: "invoices",
    },
    {
      id: "night",
      time: "بالليل",
      module: "مركز التنبيهات",
      title: "البرنامج مقفول، والخبر وصلك.",
      body: "أول ما تفعّل الإشعارات، الأسطى يخلّص والخبر يوصلك على موبايلك. وطلب من موقع شركتك بيوصل «جديد» — وما بيلمس المخزون ولا الدفاتر لحد ما إنت تأكّده.",
      points: [
        "على الآيفون: بعد ما تضيف الريّس للشاشة الرئيسية",
        "موقع إلكتروني جاهز لشركتك بيعرض مخزونك ويستقبل الطلبات",
        "سجل للعمليات المهمة ولكل دخول على حساب الشركة",
      ],
      screen: "push",
    },
  ],
};

// The steps mirror the sign-up form field for field (AuthPage.js STEPS and
// its three step bodies), including the terms box on step 3.
export const HOW = {
  eyebrow: "إزاي تبدأ",
  title: "3 خطوات، والحساب شغّال على طول.",
  subtitle: "دي الخطوات اللي هتشوفها في صفحة التسجيل، بنفس أسمائها.",
  steps: [
    { title: "بيانات الشركة", body: "اسم الشركة، القطاع، حجم الشركة، الدولة، المدينة، رقم الشغل، والعنوان." },
    { title: "حساب المسؤول", body: "اسمك، إيميلك، وكلمة سر." },
    { title: "التحقق والإطلاق", body: "لوجو ووثيقة تحقق لو عندك — اختياري. توافق على الشروط، تضغط إنشاء، وتدخل على بورد الريّس." },
  ],
  whatIfTitle: "ولو وقفت؟",
  // Two sentences on purpose: the trial has no grace; the grace applies only
  // to a paid period that ended. Data is kept on lock: nothing purges a
  // locked company (no retention job exists), and /renew promises reopening
  // «دون فقد أي بيانات» (Pages/Billing/BillingPages.jsx).
  whatIf: [
    "الشهر المجاني بيخلص بعد {trialDays} يوم: الحساب بيتقفل لحد ما تشترك، وبياناتك فاضلة.",
    "الاشتراك المدفوع لما يخلص: الحساب بيفضل شغّال {graceDays} أيام فترة سماح، وبعدها يتقفل.",
    "أول ما تدفع بيرجع يشتغل على طول، ولو اشتراكك لسه شغّال المدة الجديدة بتتحسب من آخره.",
  ],
  // The same answer with no number in it, for when the days are not known (the content
  // request failed, or a line was edited away). It must stay true at any trial or grace.
  whatIfPlain: "لما الفترة المجانية أو الاشتراك يخلصوا، الحساب بيتقفل لحد ما تشترك، وبياناتك فاضلة. أول ما تدفع بيرجع يشتغل على طول.",
  ctaText: "ابدأ شهرك المجاني", // fitTrial changes it when the trial is not a month
};

export const WHO = {
  eyebrow: "لمين الريّس",
  title: "للريّس اللي شغله بيضيع في الواتساب.",
  forTitle: "ليك لو",
  forItems: [
    "عندك شركة تكييف في مصر وبتدير أسطوات في الشارع.",
    "شغلك النهاردة: واتساب ودفتر وإكسل ومكالمات.",
    "عايز تشوف الفلوس والعهدة والمشاوير من شاشة واحدة.",
    "بتبيع أجهزة لتجار أو بتركّب للزباين — أو الاتنين.",
  ],
  notTitle: "مش ليك لو",
  notItems: [
    "محتاج البرنامج يشتغل من غير إنترنت — الريّس شغّال من المتصفح، ومفيش وضع أوفلاين.",
    "عايز رسايل واتساب تتبعت لوحدها للزباين — الريّس بيجهّزلك الرسالة والرابط، وإنت اللي تبعت.",
    "بتفوتر بغير الجنيه — الاشتراك والأسعار بالجنيه المصري.",
    "محتاج كذا فرع في حساب واحد — مش موجود دلوقتي.",
  ],
};

export const FAQ = {
  eyebrow: "أسئلة",
  title: "قبل ما تضغط ابدأ.",
  // Under the heading: where a question that isn't listed goes.
  more: "سؤالك مش هنا؟",
  moreLink: "كلّمنا",
  items: [
    {
      id: "card",
      q: "هطلب كارت في التجربة؟",
      a: "لأ. {trialDays} يوم بكل المزايا، وبعدها تختار تدفع أو تقف. ومفيش تجديد تلقائي — كل فترة دفعة واحدة.",
    },
    {
      id: "docs",
      q: "لازم أرفع سجل تجاري؟",
      a: "اختياري. اللوجو ووثيقة التحقق مش شرط للتسجيل.",
    },
    {
      id: "app",
      q: "هو في أبلكيشن؟",
      a: "الريّس شغّال من المتصفح على أي موبايل، وتقدر تضيفه على الشاشة الرئيسية زي التطبيق. والإشعارات بتوصل حتى والبرنامج مقفول بعد ما تفعّلها — وعلى الآيفون بعد ما تضيفه للشاشة الرئيسية.",
    },
    {
      id: "techs",
      q: "الأسطوات هيشتغلوا من الموبايل؟",
      a: "أيوه. «مهامي» للمشاوير، و«حضوري وراتبي» للحضور. والأسطى ما بيشوفش فلوس الشركة ولا حساباتها.",
    },
    {
      id: "data",
      q: "مين بيشوف بياناتي؟",
      a: "شركتك بس. بيانات كل شركة معزولة عن غيرها، والصلاحيات لكل موظف على مستوى الموديول، والعمليات المهمة وكل دخول بتتسجّل باسم اللي عملها ووقتها.",
    },
    {
      id: "stop",
      q: "لو وقفت الاشتراك بياناتي بتروح؟",
      a: "لأ. الحساب بيتقفل لحد ما تجدّد، ولما تدفع بيرجع على طول ببياناته.",
    },
    {
      id: "tracking",
      q: "التتبع على الخريطة شغّال دايمًا؟",
      a: "لأ. مكان الأسطى بيتشارك وقت الحضور بس، وبيقف لوحده عند الانصراف — والمتصفح لازم يكون مفتوح على موبايله.",
    },
    {
      id: "support",
      q: "لو وقفت معايا حاجة، مين يرد عليا؟",
      a: "الدعم جوّه البرنامج من أول يوم في التجربة: تفتح تذكرة، وتتابع الرد عليها لحظة بلحظة.",
    },
    {
      id: "old-data",
      q: "وبياناتي القديمة اللي في الدفتر؟",
      a: "بتدخّلها من جوّه البرنامج: الأصناف والزباين، والأرصدة الافتتاحية لكل حساب. ولو احتجت مساعدة، افتح تذكرة دعم.",
    },
    {
      id: "custom",
      q: "عندي كذا فرع أو محتاج حاجة مخصوصة؟",
      a: "كلّمنا من الفورم اللي تحت، وهنرد عليك.",
    },
  ],
};

// `{trialDays}` and `{graceDays}` come from the live pricing overlay
// (utils/plans.js TRIAL_DAYS, utils/subscriptionState.js GRACE_DAYS). A
// sentence whose number is unknown is dropped rather than guessed.
export const fillVars = (s, vars = {}) => {
  const text = String(s || "");
  let missing = false;
  const out = text.replace(/\{(\w+)\}/g, (_, k) => {
    if (vars[k] == null || vars[k] === "") { missing = true; return ""; }
    return String(vars[k]);
  });
  return missing ? "" : out;
};

// The free trial in words: «الشهر المجاني» only while it really is a month. Its length is
// the owner's (Settings); the back end's copy of these is in utils/plans.js. CMS text was
// seeded with the month, so fitTrial rewrites what is stored as well as what is here.
const MONTH_DAYS = 30;
const isMonth = (days) => !Number(days) || Number(days) === MONTH_DAYS;
export const trialPhrase = (days) => (isMonth(days) ? "الشهر المجاني" : `${days} يوم`);
export const trialCta = (days) => (isMonth(days) ? "ابدأ شهرك المجاني" : `ابدأ ${days} يوم مجانًا`);
export const fitTrial = (text, days) => {
  if (!text || isMonth(days)) return text;
  return String(text)
    .split("ابدأ شهرك المجاني").join(trialCta(days))
    .split("الشهر المجاني بيخلص بعد").join("التجربة المجانية بتخلص بعد")
    .split("شهرًا مجانًا").join(`${days} يوم مجانًا`)
    .split("في الشهر المجاني").join(`في الـ${days} يوم المجانية`)
    .split("الشهر المجاني").join(trialPhrase(days));
};

// Every "start" link goes to the sign-up tab. A stored "/auth" (the old
// default) is read the same way — it opened on login.
export const SIGNUP_HREF = "/auth?mode=register";
export const signupHref = (href) => (!href || href === "/auth" ? SIGNUP_HREF : href);
